import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import Logo from "../components/Logo";
import { Calendar, MapPin, Fingerprint, ArrowRight, ShareNetwork, DownloadSimple, Ticket } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PublicEvent() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/events/${eventId}`);
        setEvent(data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const sharePoster = async () => {
    if (!event?.poster_url) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: event.title,
          text: `Check out ${event.title} on AURA`,
          url: window.location.href,
        });
      } else {
        await copyLink();
      }
    } catch {}
  };

  const downloadPoster = () => {
    if (!event?.poster_url) return;
    const a = document.createElement("a");
    a.href = event.poster_url;
    a.download = `${event.title.replace(/\s+/g, "_")}_poster.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="mono-font text-sm text-white/50 uppercase tracking-widest pulse-glow">Loading event…</div>
      </div>
    );
  }
  if (notFound || !event) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-6 p-6 text-white">
        <Logo size={44} />
        <div className="heading-font text-2xl">Event not found or unavailable.</div>
        <Link to="/" className="mono-font text-xs uppercase tracking-[0.25em] text-[#00FF66] hover:text-white">← Home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="public-brand"><Logo size={38} /></Link>
          <Link to="/login" data-testid="public-login-btn" className="mono-font text-xs uppercase tracking-[0.25em] text-white/70 hover:text-white">
            Log in
          </Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10 md:py-16 grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-14">
        <div className="border border-white/10 bg-[#0A0A0C] overflow-hidden self-start">
          {event.poster_url ? (
            <img src={event.poster_url} alt={event.title} className="w-full h-auto object-cover" data-testid="public-poster" />
          ) : event.design_url ? (
            <img src={event.design_url} alt={event.title} className="w-full h-auto object-cover" />
          ) : (
            <div className="aspect-[4/5] aura-grid flex items-center justify-center">
              <Ticket size={64} weight="duotone" className="text-white/20" />
            </div>
          )}
        </div>

        <div className="flex flex-col justify-center">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 flex items-center gap-2">
            <Fingerprint size={12} weight="fill" className="text-[#00FF66]" /> Cryptographic event · AURA
          </div>
          <h1 className="heading-font text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-4" data-testid="public-event-title">
            {event.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 mono-font text-sm text-white/70">
            {event.date && (
              <div className="flex items-center gap-2">
                <Calendar size={16} weight="duotone" className="text-[#00FF66]" /> {event.date}
              </div>
            )}
            {event.venue && (
              <div className="flex items-center gap-2">
                <MapPin size={16} weight="duotone" className="text-[#00FF66]" /> {event.venue}
              </div>
            )}
          </div>
          {event.description && (
            <p className="mt-6 text-white/70 leading-relaxed max-w-2xl">{event.description}</p>
          )}
          <div className="mt-6 mono-font text-xs text-white/50" data-testid="public-event-host">
            Hosted by <span className="text-white">{event.host || event.creator_name}</span>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={sharePoster} data-testid="public-share-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
              <ShareNetwork size={14} weight="bold" /> Share
            </button>
            <button onClick={copyLink} data-testid="public-copy-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
              Copy Link
            </button>
            {event.poster_url && (
              <button onClick={downloadPoster} data-testid="public-download-poster"
                className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
                <DownloadSimple size={14} weight="bold" /> Download Poster
              </button>
            )}
          </div>
          <div className="mt-10 border border-white/10 p-6">
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Get a ticket</div>
            <p className="mt-2 text-sm text-white/70">
              Tickets are distributed exclusively through authorized AURA members. Reach out to a member you know or contact the host directly.
            </p>
            <Link to="/login" className="mt-4 inline-flex items-center gap-2 mono-font text-xs uppercase tracking-[0.25em] text-[#00FF66] hover:text-white transition-colors duration-200">
              Are you a distributor? Log in <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">
            AURA · Automated Unified Receipt Architecture
          </div>
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">
            © 2026 Zayne Crouse · Patent DTTM-001
          </div>
        </div>
      </footer>
    </div>
  );
}
